import { BodyBox, Text } from '@lbcamden/lib-react';
import { formatDMY, formatOrFallback } from '@lbcamden/shared-utils';
import sumBy from 'lodash/sumBy';
import { FunctionComponent } from 'react';
import { useParams } from 'react-router';

import {
  applicationNominationFlagDisplayValues,
  applicationOfferFlagDisplayValues,
} from '~/client/config/display-values/housingApplications';
import {
  useApplicationAllocationList,
  useApplicationApplicantsList,
  useEntityNoteList,
  useHousingApplication,
  useProperty,
  useReferenceListDisplayValue,
  useUserStore,
} from '~/client/hooks/storeHooks';
import { getPropertyBreadcrumbs } from '~/client/utils/navigation';
import { defaultFallbackValue } from '~/common/config/constants';

import { InfoItem, ItemDetails } from '../../common/ItemDetails';
import { PageTitle } from '../../common/PageTitle';
import { NotesTable } from '../../shared/NotesTable';
import { PageBreadcrumbs } from '../../shared/PageBreadcrumbs';
import { LoadInNotFoundPage } from '../LoadInNotFoundPage';

export const HousingApplicationPage: FunctionComponent = () => {
  const { propertyId: propertyIdParam, applicationId: applicationIdParam } = useParams();
  const propertyId = parseInt(propertyIdParam!);
  const applicationId = parseInt(applicationIdParam!);

  const userStore = useUserStore();

  const property = useProperty(propertyId);
  const referenceListDisplayValue = useReferenceListDisplayValue();
  const application = useHousingApplication(applicationId);
  const applicants = useApplicationApplicantsList(applicationId);
  const allocations = useApplicationAllocationList(applicationId);
  const applicationNotes = useEntityNoteList('APP', applicationId);

  if (!property || !application) {
    return <LoadInNotFoundPage />;
  }

  const pageTitle = `Application ${applicationId}`;

  const getPersonFromId = (personId: number) => userStore?.get('people', personId);

  const getTitle = () => {
    const applicantNames = applicants
      .map(({ personId }) => getPersonFromId(personId)?.fullName)
      .join(', ');

    return (
      `${applicantNames} - ${formatOrFallback(application.receivedDate, formatDMY)} - ` +
      `${referenceListDisplayValue('STATUS_CODE', application.status)}`
    );
  };

  return (
    <BodyBox>
      <PageTitle title={pageTitle} />
      <PageBreadcrumbs links={[...getPropertyBreadcrumbs(property), { name: pageTitle }]} />
      <ItemDetails
        css={{ paddingBottom: 0 }}
        titleLabel="Application"
        title={getTitle()}
        sections={[
          {
            title: 'Application details',
            details: [
              ['Application reference number', application.applicationId],
              ['Tenancy reference', application.tenancyId],
              [
                'Application date',
                formatOrFallback(application.receivedDate, formatDMY, defaultFallbackValue),
              ],
              ['Status', referenceListDisplayValue('STATUS_CODE', application.status)],
              [
                'Status last changed',
                formatOrFallback(application.statusDate, formatDMY, defaultFallbackValue),
              ],
              ['Offer flag', applicationOfferFlagDisplayValues[application.offerFlag]],
              [
                'Nomination flag',
                applicationNominationFlagDisplayValues[application.nominationFlag],
              ],
              ['Correspondence name', application.correspondenceName],
              [
                'Homeless reason',
                referenceListDisplayValue('HCREASON', application.homelessReason),
              ],
              [
                'Homeless case origin',
                referenceListDisplayValue('HCORIGIN', application.homelessCaseOrigin),
              ],
              [
                'Accepted as homeless date',
                formatOrFallback(application.acceptedHomelessDate, formatDMY, defaultFallbackValue),
              ],
              [
                'Expected to be homeless date',
                formatOrFallback(application.expectedHomelessDate, formatDMY, defaultFallbackValue),
              ],
              ['Application source', application.applicationSource],
              ['Advice case reference', application.adviceCaseRef],
            ],
          },
          {
            title: 'Points allocated',
            details: [
              ...allocations.map<InfoItem>(({ rule, ruleDescription, points }) => [
                `${ruleDescription} (${rule})`,
                points,
              ]),
              [
                // oxlint-disable-next-line react/jsx-key -- prop tuple, not rendered children
                <Text $weight={500}>Total points</Text>,
                // oxlint-disable-next-line react/jsx-key -- prop tuple, not rendered children
                <Text $weight={500}>{sumBy(allocations, 'points')}</Text>,
              ],
            ],
          },
        ]}
      />
      <NotesTable propertyId={propertyId} notes={applicationNotes} typeFilterOptions={[]} />
    </BodyBox>
  );
};
